/**
 * Shared React Query key factory i hook-ovi za CSO / public-body list i approval API-je.
 * staleTime 60s — navigacija između admin tabova ne pokreće odmah novi refetch.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import CSOProfileService from '@/shared/services/cso/CSOProfileService';
import CSOApplicationService from '@/shared/services/cso/CSOApplicationService';
import PublicBodyService from '@/shared/services/cso/PublicBodyService';

/** Query key factory za CSO i public-body keš. */
export const csoKeys = {
  all: ['cso'],
  organizations: () => [...csoKeys.all, 'organizations'],
  approvalList: () => [...csoKeys.all, 'approval'],
  publicBodies: () => [...csoKeys.all, 'publicBodies'],
  approvalListPublicBodies: () => [...csoKeys.all, 'approvalPublicBodies'],
};

const STALE_TIME_MS = 60 * 1000;

/**
 * React Query hook za listu verifikovanih CSO organizacija.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useCSOOrganizationsQuery(options = {}) {
  return useQuery({
    queryKey: csoKeys.organizations(),
    queryFn: () => CSOProfileService.getAllOrganizations(),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * React Query hook za CSO approval listu (organizacije na čekanju).
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useCSOApprovalListQuery(options = {}) {
  return useQuery({
    queryKey: csoKeys.approvalList(),
    queryFn: () => CSOApplicationService.getApprovalList(),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * React Query hook za listu verifikovanih javnih tela.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function usePublicBodiesQuery(options = {}) {
  return useQuery({
    queryKey: csoKeys.publicBodies(),
    queryFn: () => PublicBodyService.getAllPublicBodies(),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * React Query hook za approval listu javnih tela (na čekanju).
 *
 * @param {import('@tanstack/react-query').UseQueryOptions} [options] - Dodatne React Query opcije.
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function usePublicBodiesApprovalListQuery(options = {}) {
  return useQuery({
    queryKey: csoKeys.approvalListPublicBodies(),
    queryFn: () => PublicBodyService.getApprovalListPublicBodies(),
    staleTime: STALE_TIME_MS,
    ...options,
  });
}

/**
 * Vraća QueryClient instancu (helper za cache operacije u CSO modulu).
 *
 * @returns {import('@tanstack/react-query').QueryClient}
 */
export function useCSOQueryClient() {
  return useQueryClient();
}
